import { StatusId, OperationMS, TabDescriptor, TabKind } from '../types';
import { messagerieOngletDisabled, prestatairesValides } from './messagerieGating';
import { isAnalyseValidee } from './analyse/historique';

// Onglets ACHETEUR de l'operation MS + leur gating, PAR ID de statut (process actuel).
// Les ids ne suivent pas l'ordre du cycle de vie -> ensembles explicites, pas de `>=`.
// L'onglet Messagerie delegue a messagerieGating (source unique partagee avec TabMessagerie).

interface TabDef {
  kind: TabKind;
  label: string;
}

const TAB_DEFS: TabDef[] = [
  { kind: 'historique', label: 'Historique' },
  { kind: 'consultation', label: 'Consultation' },
  { kind: 'questions_reponses', label: 'Questions et réponses' },
  { kind: 'analyse', label: 'Analyse' },
  { kind: 'notifications', label: 'Notifications et signatures' },
  { kind: 'messagerie', label: 'Messagerie' },
  { kind: 'evaluation', label: 'Évaluation' },
];

// Gating fidele au legacy getDisabledTabs (MS) : seuils de statut + prestataires valides +
// garde annulation. On garde des SEUILS (pas des ensembles d'ids) car ils couvrent les DEUX
// modes de signature : manuscrite (13 "A signer" -> 11 Valide) ET automatique (ballet 7/8/9/10).
const tabDisabled = (kind: TabKind, operation: OperationMS): boolean => {
  const sid = operation.status?.id ?? 0;
  const hasValidPrestataires = prestatairesValides(operation).length > 0;
  const annuleSansAnalyse = sid === StatusId.Annule && !isAnalyseValidee(operation.historique);

  switch (kind) {
    // Historique et Consultation ne sont jamais grises.
    case 'historique':
    case 'consultation':
      return false;
    // Questions/reponses : verrouille tant que la consultation n'est pas lancee (statut >= 2).
    case 'questions_reponses':
      return sid < StatusId.ConsultationEnCours;
    // Evaluation : grisee tant que l'operation n'est pas signee (statut 11 Valider).
    case 'evaluation':
      return sid !== StatusId.Valider;
    case 'analyse':
      // Active des que la consultation est lancee (statut >= 2).
      return sid < StatusId.ConsultationEnCours || !hasValidPrestataires || annuleSansAnalyse;
    case 'notifications':
      // Active une fois l'analyse atteinte (statut >= 5) -> couvre les deux modes de signature.
      return sid < StatusId.EnAttenteAnalyse || !hasValidPrestataires || annuleSansAnalyse;
    case 'messagerie':
      return messagerieOngletDisabled(operation); // s'active au statut 11 (signe)
    default:
      return true;
  }
};

// Plus d'onglet inactif cote MS : Evaluation est desormais active (grisee tant que non signee).
const tabInactive = (_kind: TabKind): boolean => false;

export const buildOperationTabs = (operation: OperationMS): TabDescriptor[] =>
  TAB_DEFS.map((t) => ({
    key: t.kind,
    kind: t.kind,
    label: t.label,
    disabled: tabDisabled(t.kind, operation),
    inactive: tabInactive(t.kind),
  }));

// Onglet actif par defaut selon le statut (l'onglet le plus pertinent du moment).
// Phase signature couverte pour les DEUX modes : manuscrite (13) ET automatique (7/8/9/10),
// plus 11 (signe). Les autres etats (refus/expire/annule) retombent sur Historique.
export const computeDefaultTabKind = (operation: OperationMS): TabKind => {
  switch (operation.status?.id) {
    case StatusId.Brouillon:
    case StatusId.ConsultationEnCours:
      return 'consultation';
    case StatusId.AAnalyser:
      return 'analyse';
    case StatusId.ASigner: // 7 (mode automatique)
    case StatusId.EnAttenteSignaturePrestataire: // 8
    case StatusId.EnAttenteSignatureAcheteur: // 9
    case StatusId.ASignerPrestataire: // 10
    case StatusId.ANotifier: // 13 A signer (mode manuscrite)
    case StatusId.Valider: // 11 signe
      return 'notifications';
    default:
      return 'historique';
  }
};
